import { DestroyRef, Directive, ElementRef, afterNextRender, inject, signal } from '@angular/core';

/**
 * Exposes the height left between the element's top and the bottom of the viewport (minus the
 * surrounding `main` bottom padding) as the `--fill-viewport-height` custom property. Re-measured
 * whenever the shell resizes, so content settling above the element keeps it in sync.
 */
@Directive({
  selector: '[membersFillViewportHeight]',
  standalone: true,
  host: { '[style.--fill-viewport-height]': 'height()' },
})
export class FillViewportHeightDirective {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly height = signal<string | null>(null);

  constructor() {
    afterNextRender(() => {
      const shell = this.element.nativeElement.closest('main') ?? document.body;
      const observer = new ResizeObserver(() => this.measure());

      observer.observe(shell);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  private measure(): void {
    const element = this.element.nativeElement;
    const main = element.closest('main');
    const top = element.getBoundingClientRect().top + window.scrollY;
    const bottomPadding = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;

    this.height.set(`calc(100dvh - ${top + bottomPadding}px)`);
  }
}
